// Lightweight client for the Bloom DPP JWT API (brand & user logins).
// Separate from the legacy Cognito AuthProvider used by the jewellery flow.
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
export const DPP_BASE_URL = process.env.REACT_APP_DPP_BASE_URL || "http://localhost:3000";

// Format any date/datetime for display as "28th July, 2025" — day ordinal, full
// month, year; time is dropped. Date-only strings are parsed without a timezone
// shift so the day never moves. Returns '' for empty and the raw value if unparseable.
export const formatDate = (value) => {
    if (!value) return '';
    const iso = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const d = iso ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) : new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const day = d.getDate();
    const k = day % 100, j = day % 10;
    const suffix = (k >= 11 && k <= 13) ? 'th' : j === 1 ? 'st' : j === 2 ? 'nd' : j === 3 ? 'rd' : 'th';
    return `${day}${suffix} ${d.toLocaleString('en-US', { month: 'long' })}, ${d.getFullYear()}`;
};

// Tokens are kept per role so a brand and a user can be logged in independently.
export const getToken = (role) => localStorage.getItem(`bloom_${role}_token`);
export const setToken = (role, token) => localStorage.setItem(`bloom_${role}_token`, token);
export const clearToken = (role) => localStorage.removeItem(`bloom_${role}_token`);

// ── Session expiry ────────────────────────────────────────────────────────────
// Backend tokens live for TOKEN_TTL_HOURS (24h — routes/brands.py, routes/users.py).
// Past that, every request 401s with "invalid token". A stale token is still a
// *present* token, so a plain `if (!token)` guard waves it straight through and the
// user lands on a dashboard that fails every call with a cryptic error instead of
// being asked to log in again. Expiry is therefore handled HERE, once, for every
// authenticated call — not per component.

export const LOGIN_PATH = { brand: "/brand", user: "/user" };

export const SESSION_EXPIRED = "Your session has expired. Please log in again.";

// Read a JWT's payload WITHOUT verifying it. The server remains the only authority
// on validity; this is purely so the client can notice its own token is spent and
// stop using it. (JWT payload is base64url — atob needs it converted.)
export function decodeToken(token) {
    try {
        const payload = token.split(".")[1];
        return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    } catch {
        return null;
    }
}

// `exp` is seconds since epoch. Unreadable/absent -> assume valid and let the
// server decide; we never want a client-side parse bug to lock someone out.
export function isTokenExpired(token) {
    const exp = token && decodeToken(token)?.exp;
    return exp ? exp * 1000 <= Date.now() : false;
}

// Drop the session and send them to the right login page. The role comes from the
// token's own `role` claim, so no caller has to tell us which one it was.
export function logout(role, { redirect = true } = {}) {
    if (role === "brand" || role === "user") {
        clearToken(role);
    } else {
        clearToken("brand");   // unreadable token: don't guess, clear both
        clearToken("user");
    }
    if (redirect) window.location.assign(LOGIN_PATH[role] || "/");
}

export async function apiFetch(path, { method = "GET", token, body, isForm = false, logoutOn401 = true } = {}) {
    // Already spent — log out now rather than burn a round trip to be told so.
    if (token && logoutOn401 && isTokenExpired(token)) {
        logout(decodeToken(token)?.role);
        throw new Error(SESSION_EXPIRED);
    }

    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (body && !isForm) headers["Content-Type"] = "application/json";

    const res = await fetch(`${API_BASE_URL}${path}`, {
        method,
        headers,
        body: isForm ? body : body ? JSON.stringify(body) : undefined,
    });

    // A 401 on a request we DID authenticate means the token is no longer good
    // (expired, or the signing secret rotated). Only then — a 401 from /api/login
    // is just bad credentials, and there is no session to end.
    if (res.status === 401 && token && logoutOn401) {
        logout(decodeToken(token)?.role);
        throw new Error(SESSION_EXPIRED);
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
}

// Fire-and-forget click tracking for major-component usage (MVP — storage
// only, see backend/models.UsageEvent). Never throws and never awaited by
// callers, so a tracking failure can't affect the UI. Forwards this page's
// own ?preview=1 (if present) so brand/demo sessions are flagged as test
// traffic the same way passport views are.
export function track(role, component, detail) {
    const token = getToken(role);
    if (!token) return;
    const preview = new URLSearchParams(window.location.search).get("preview") === "1" ? "?preview=1" : "";
    apiFetch(`/api/${role}/track${preview}`, {
        method: "POST", token, body: { component, detail },
    }).catch(() => {});
}
